import _ from "lodash";
import React from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import I18nHOC from "UIComponents/I18nHOC";
import EmptyField from "UIComponents/EmptyField";
import EmptyView from "UIComponents/EmptyView";
import { DragAndDropList as DRAG_AND_DROP_LIST } from "@toddle-edu/ds-web";
import { nanoid } from "nanoid";
import { NoFoldersIllustration } from "@toddle-edu/ds-theme";
import UIInputList from "./UIInputList";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import classes from "./DragAndDropList.scss";
import GenericListView from "UIComponents/GenericListView";

class DragAndDropList extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      localValue: props.value,
    };

    this.debouncedUpdateValue = _.debounce(this.updateValue, 150);
  }

  // Handle immediate local updates for smooth typing
  handleValueChange = newArr => {
    // Update state for immediate UI response
    this.setState({ localValue: newArr });
    // Debounce the actual state update to avoid excessive renders
    this.debouncedUpdateValue(newArr);
  };

  componentDidMount = () => {
    const { value } = this.props;

    if (value.length == 0) {
      this.handleInit();
    }

    if (this.shouldShowEditEmpty()) {
      this.updateValue([{ id: nanoid(10), value: "" }]);
    }
  };

  handleInit = () => {
    const { options, shouldInitOptions } = this.props;
    if (shouldInitOptions && options.length > 0) {
      const newValue = _.map(options, option => {
        return { id: option.value, value: option.label, isSelected: false };
      });
      this.updateValue(newValue);
    } else {
      this.updateValue([{ id: nanoid(10), value: "" }]);
    }
  };

  // Update local state when props change (e.g., from other users)
  componentDidUpdate(prevProps) {
    const { value } = this.props;

    if (!_.isEqual(prevProps.value, value)) {
      this.setState({ localValue: value });
    }

    if (
      _.get(this.props.value, "length", 0) == 0 ||
      _.size(_.differenceBy(prevProps.options, this.props.options, "id")) > 0 ||
      !_.isEqual(_.size(prevProps.options), _.size(this.props.options))
    ) {
      this.handleInit();
    }
  }

  shouldShowEditEmpty = () => {
    const { options, shouldInitOptions } = this.props;
    if (shouldInitOptions) {
      return _.get(options, "length", 0) === 0;
    } else {
      return false;
    }
  };

  renderEditEmpty = () => {
    const { emptyText, emptyEditText, isDependentField } = this.props;
    return isDependentField ? (
      <span
        className={classnames("text-body", classes.emptyText, {
          [classes.emptyTextDependentField]: isDependentField,
        })}
      >
        {emptyEditText || emptyText}
      </span>
    ) : (
      <EmptyField title={emptyEditText || emptyText} />
    );
  };

  renderDisabledEditEmpty = () => {
    const { emptyText, disabledEmptyComponent } = this.props;
    if (disabledEmptyComponent) {
      return disabledEmptyComponent;
    }
    return (
      <EmptyView
        emptyImageUrl={NoFoldersIllustration}
        emptyHeaderText={emptyText}
        size="small"
      />
    );
  };

  shouldShowViewEmpty = () => {
    const { value, showListItemNumber } = this.props;
    const filteredValue = showListItemNumber
      ? value
      : _.filter(value, item => item.isSelected);

    // return true if value is empty or if value has only one item and that item is empty
    return (
      _.isEmpty(filteredValue) ||
      (_.size(filteredValue) === 1 && _.isEmpty(value[0]?.value))
    );
  };

  renderView = () => {
    const { value, showListItemNumber } = this.props;
    const filteredValue = showListItemNumber
      ? value
      : _.filter(value, item => item.isSelected);
    return (
      <GenericListView
        nodes={value}
        rootNodeIds={_.map(filteredValue, "id")}
        type={showListItemNumber ? "ordered" : "unordered"}
        shouldEmphasisRootNodes={false}
      />
    );
  };

  renderEdit = () => {
    const {
      value,
      fieldKey,
      showListItemNumber,
      t,
      isDisabled = false,
      testId,
      alwaysShowDragIcon,
      showDeleteItemButton,
      paddingY,
      dragIconStyles,
      displayDisabledAsActive,
      dsVersion,
      showSeparator,
    } = this.props;

    const { localValue } = this.state;
    const currentValue = localValue || value;

    const showDisabledAsActive = isDisabled && displayDisabledAsActive;

    const isValueEmpty =
      _.isEmpty(currentValue) ||
      _.every(currentValue, item => _.isEmpty(item?.value));

    /*******************************************************************************************************
     * displayDisabledAsActive flag is used to control the visual state of this component,
     * where if it is true, we will show the element in visually active form but
     * functionality wise it will behave like disabled
     ********************************************************************************************************/
    if (isDisabled && !displayDisabledAsActive) {
      return (
        <div style={{ pointerEvents: "none", flex: 1 }}>
          {_.size(currentValue) && currentValue?.[0]?.value ? (
            <UIInputList
              value={currentValue}
              mode="view"
              showListItemNumber={false}
              isDNDDisabled={true}
            />
          ) : (
            this.renderDisabledEditEmpty()
          )}
        </div>
      );
    }

    if (isDisabled && isValueEmpty) {
      return this.renderDisabledEditEmpty();
    }

    return (
      <ConditionalWrapper
        condition={showDisabledAsActive}
        wrapper={<div className={classes.disabledAsActive} />}
        type={"wrapperAsElement"}
      >
        <DRAG_AND_DROP_LIST
          value={currentValue}
          dndKey={`UI_INPUT_LIST:${fieldKey}`}
          onValueChange={this.handleValueChange}
          listType={showListItemNumber ? "numberList" : "checklist"}
          itemPlaceholder={
            showDisabledAsActive
              ? t("common:not_defined")
              : t("common:typeHere")
          }
          addItemText={t("common:add_another")}
          testId={testId}
          alwaysShowDragIcon={alwaysShowDragIcon}
          showDeleteItemButton={showDeleteItemButton}
          paddingY={paddingY}
          dragIconStyles={dragIconStyles}
          showAddAnotherItemButton={!isDisabled || !displayDisabledAsActive}
          mode={showDisabledAsActive ? "view" : "edit"}
          dsVersion={dsVersion}
          showSeparator={showSeparator}
        />
      </ConditionalWrapper>
    );
  };
}

DragAndDropList.defaultProps = {
  ...UIBaseComponent.defaultProps,
  showListItemNumber: true,
  shouldInitOptions: false,
  displayDisabledAsActive: false,
  dsVersion: "1.0",
  showSeparator: true,
};

DragAndDropList.propTypes = {
  ...UIBaseComponent.propTypes,
  showListItemNumber: PropTypes.bool,
  shouldInitOptions: PropTypes.bool,
  displayDisabledAsActive: PropTypes.bool,
  dsVersion: PropTypes.string,
  showSeparator: PropTypes.bool,
  disabledEmptyComponent: PropTypes.node,
};

export default I18nHOC({ resource: "common" })(DragAndDropList);
